<script setup lang="ts">
import { useRoute } from "vue-router";
import { onBeforeMount, ref } from "vue";
import { useApiClient } from "@/api/client";
import SegmentConfiguration from "@/modules/segments/pages/SegmentConfiguration.vue";
import { loadingWrapperAll } from "@/api/helpers/loadingWrapper";
import DocumentDO = App.Data.Explorer.DocumentDO;
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { useSegmentBuilderStore } from "@/modules/segments/store";
import {
	loadNewPart,
	loadNewSegment,
} from "@/modules/segments/pages/SegmentUtils";
import DimensionDO = App.Data.DimensionDO;
import SegmentType = App.Enums.SegmentType;
import dayjs from "dayjs";
const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;
const getDataSourceForFilter = useSegmentBuilderStore().getDataSourceForFilter;

const route = useRoute();

const resource_uuid: string | null =
	route.params.resource_uuid !== "" ? String(route.params.resource_uuid) : null;
const resource_type: string | string[] =
	route.params.resource_type !== "" ? String(route.params.resource_type) : null;
const segmentType = (route.query.type as string) || "customer";

const isLoading = ref(true);

const isUnsavedSegmentFromExplorer = ref(false);

const allAvailableDimensions = ref([] as DimensionDO[]);

const convertDocumentPeriodToSubjectDateFilter = async (
	document: DocumentDO
): Promise<ActiveFilterDTO | null> => {
	const response = await useApiClient("segments").getDocumentPeriodFilter(
		document.uuid
	);

	return response.isOk() ? response.value : null;
};

onBeforeMount(async () => {
	const apiClientType = resource_type === "segment" ? "segments" : "documents";

	// Fetch resource first so we can use the segment's actual type for totalBase
	const resourceResponse = await useApiClient(apiClientType).show(
		resource_uuid
	);

	const effectiveType =
		resource_type === "segment" && resourceResponse.isOk()
			? resourceResponse.value.type
			: segmentType;

	const [dimensionsResponse, totalBaseResponse, verticalsResponse] =
		await loadingWrapperAll([
			useApiClient("segments").getDimensions({
				"filter[is_filterable]": true,
				"filter[domain]": "parking",
			}),
			useApiClient("segments").totalBase(effectiveType as SegmentType),
			useApiClient("segments").getVerticals({
				"filter[is_summarizable_in_segments]": true,
				"filter[include_hidden]": true,
			}),
		]);

	if (dimensionsResponse.isOk()) {
		allAvailableDimensions.value = dimensionsResponse.value;
	}

	if (totalBaseResponse.isOk()) {
		segmentBuilderState.totalSegmentBase = totalBaseResponse.value.count;
	}

	if (verticalsResponse.isOk()) {
		segmentBuilderState.availableMetrics = verticalsResponse.value;
	}

	if (resourceResponse.isOk()) {
		if (resource_type === "segment") {
			segmentBuilderState.segment = resourceResponse.value;
		} else {
			// resource === document
			const documentSubjectDayAsFilter =
				await convertDocumentPeriodToSubjectDateFilter(resourceResponse.value);

			const parts = [];

			Object.entries(
				groupFiltersByDataSource([
					documentSubjectDayAsFilter,
					...resourceResponse.value.active_filters,
				])
			).forEach(([dataSource, activeFilters]) => {
				const part = loadNewPart(dataSource, "include", activeFilters);
				parts.push(part);
			});

			isUnsavedSegmentFromExplorer.value = true;

			segmentBuilderState.segment = loadNewSegment(
				parts,
				segmentType as SegmentType
			);
			segmentBuilderState.segment.title = `From explorer ${dayjs().format(
				"DD MMM YYYY HH:mm"
			)}`;
		}
	}

	isLoading.value = false;
});

const groupFiltersByDataSource = (
	filters: ActiveFilterDTO[]
): Record<string, ActiveFilterDTO[]> => {
	return filters.reduce((grouped, filter) => {
		const dataSource = getDataSourceForFilter(filter);

		if (dataSource === "unknown") {
			return grouped;
		}

		if (!grouped[dataSource]) {
			grouped[dataSource] = [];
		}
		grouped[dataSource].push(filter);
		return grouped;
	}, {} as Record<string, ActiveFilterDTO[]>);
};
</script>

<template>
	<div>
		<v-progress-linear
			class="mt-10"
			v-if="isLoading"
			color="primary"
			indeterminate
			rounded
			height="6"
		/>
		<segment-configuration
			v-else
			:all-available-dimensions="allAvailableDimensions"
			:is-unsaved-segment-from-explorer="isUnsavedSegmentFromExplorer"
		/>
	</div>
</template>

<style scoped lang="scss"></style>
